<!-- app-subdomain-creators\src\Components\CreatorMediaUploadModal.vue -->

<!-- src/Components/CreatorMediaUploadModal.vue -->
<template>
  <div
    v-if="show"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
    :style="{ zIndex: resolvedZIndex }"
  >
    <div class="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg font-semibold text-zinc-800">Upload Media</h3>
        <button
          class="text-zinc-400 hover:text-zinc-600"
          @click="emit('close')"
        >
          ✕
        </button>
      </div>

      <p class="text-xs text-zinc-500 mb-4">
        Upload images, videos, or documents. Uploads will continue in the
        background so you can keep working.
      </p>

      <div
        class="border-2 border-dashed border-zinc-200 rounded-xl px-4 py-10 text-center cursor-pointer hover:border-blue-400 hover:bg-zinc-50"
        :class="isUploading ? 'pointer-events-none opacity-80' : ''"
        @dragover.prevent
        @drop.prevent="handleDrop"
        @click="openFilePicker"
      >
        <template v-if="isUploading">
          <p class="text-sm font-semibold text-zinc-700 mb-1">
            Uploading media...
          </p>
          <p class="text-xs text-zinc-500 mb-3">
            {{ uploadStatusMessage }}
          </p>
          <div class="mx-auto w-full max-w-[240px] h-2 rounded-full bg-zinc-200 overflow-hidden">
            <div
              class="h-full bg-[#2D8CFF] transition-all duration-150"
              :style="{ width: `${uploadProgress}%` }"
            />
          </div>
          <p class="mt-2 text-xs text-zinc-500">{{ uploadProgress }}%</p>
        </template>
        <template v-else>
          <p class="text-sm text-zinc-600 mb-1">
            Drag & drop files here, or <span class="text-blue-500 font-semibold">browse</span>
          </p>
          <p class="text-xs text-zinc-400">You can select multiple files.</p>
        </template>
        <input
          ref="fileInput"
          type="file"
          class="hidden"
          multiple
          @change="handleFileChange"
        />
      </div>

      <div class="mt-4 flex justify-end">
        <button
          class="px-4 py-2 text-sm rounded-lg border border-zinc-200 text-zinc-600 hover:bg-zinc-50"
          @click="emit('close')"
        >
          Close
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useMediaUploadStore } from '@/stores/mediaUploadStore';

const props = defineProps({
  show: { type: Boolean, default: false },
  folderId: { type: [Number, String, null], default: null },
  zIndex: { type: Number, default: 50 },
});

const emit = defineEmits(['close', 'uploaded']);

const fileInput = ref(null);
const resolvedZIndex = computed(() => {
  const value = Number(props.zIndex);
  return Number.isFinite(value) && value > 0 ? value : 50;
});
const uploadStore = useMediaUploadStore();
const isUploading = ref(false);
const uploadProgress = ref(0);
const uploadStatusMessage = ref('');

const openFilePicker = () => {
  if (isUploading.value) return;
  if (fileInput.value) {
    fileInput.value.value = null;
    fileInput.value.click();
  }
};

const uploadSelectedFiles = async (fileList) => {
  const files = Array.from(fileList || []);
  if (!files.length || isUploading.value) return;

  isUploading.value = true;
  uploadProgress.value = 0;
  uploadStatusMessage.value = `Preparing ${files.length} file${files.length === 1 ? '' : 's'}...`;

  let completed = 0;
  let planLimitReached = false;
  try {
    for (const file of files) {
      uploadStatusMessage.value = `Uploading ${file.name || 'file'}...`;
      uploadProgress.value = 0;
      const uploaded = await uploadStore.uploadSingleFile(file, props.folderId, {
        origin: 'creator-media-upload-modal',
        onProgress: ({ progress, status }) => {
          const nextProgress = Number.parseInt(String(progress || 0), 10) || 0;
          if (status === 'processing') {
            uploadStatusMessage.value = `Processing ${file.name || 'file'}...`;
            uploadProgress.value = 100;
            return;
          }
          uploadStatusMessage.value = `Uploading ${file.name || 'file'}...`;
          uploadProgress.value = Math.max(0, Math.min(100, nextProgress));
        },
        onError: ({ code }) => {
          if (code === 'creator_plan_limit_reached') {
            planLimitReached = true;
          }
        },
      });
      if (uploaded) {
        completed += 1;
        uploadStatusMessage.value = `Uploaded ${completed} of ${files.length}`;
        uploadProgress.value = 100;
      } else {
        uploadStatusMessage.value = planLimitReached
          ? 'Your media storage limit has been reached.'
          : `Failed to upload ${file.name || 'file'}.`;
      }
      if (planLimitReached) {
        break;
      }
    }
    if (completed > 0) emit('uploaded');
  } finally {
    isUploading.value = false;
  }
};

const handleFileChange = async (event) => {
  const files = event.target.files;
  if (files && files.length > 0) await uploadSelectedFiles(files);
};

const handleDrop = async (event) => {
  if (isUploading.value) return;
  const files = event.dataTransfer.files;
  if (files && files.length > 0) await uploadSelectedFiles(files);
};

watch(
  () => props.show,
  (open) => {
    if (open) {
      uploadProgress.value = 0;
      uploadStatusMessage.value = '';
      isUploading.value = false;
    }
  }
);
</script>
